import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, CheckCircle2, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '@/Components/ui/card';
import { Input } from '@/Components/ui/input';
import { Button } from '@/Components/ui/button';
import { Text, Heading } from '@/Components/ui/typography';
import { getApiUrl } from '@/config/api';
import { getColor } from '@/config/theme';
import { cn } from '@/lib/utils';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      const response = await fetch(getApiUrl('/api/employer-forgot-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email }),
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        setIsSuccess(true);
        setMessage('If that email has an account, a reset link is on its way. It works once and expires in 15 minutes.');
      } else {
        setIsSuccess(false);
        setMessage(data.error || 'Failed to send reset link. Please try again.');
      }
    } catch (error) {
      setIsSuccess(false);
      setMessage('An error occurred. Please check your internet connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-background text-foreground flex flex-col justify-center items-center relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--foreground)) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Logo Section - Top Left */}
      <div className="absolute top-6 left-6 md:top-8 md:left-8 z-10">
        <Link 
          to="/hirewithus"
          className="transition-opacity hover:opacity-80"
        >
          <img
            src="/Newzenalphablue.png"
            alt="SearchJob.ai Logo"
            className="h-8 md:h-10 w-auto object-contain drop-shadow-sm"
          />
        </Link>
      </div>
      
      <div className="flex justify-center items-center gap-12 max-w-7xl w-full flex-wrap px-6 py-12 md:flex-nowrap relative z-10">
        <Card className={cn(
          "flex flex-col w-full max-w-[480px] p-8 md:p-10",
          "bg-card border border-border/50",
          "shadow-xl shadow-primary/5",
          "transition-all duration-300",
          "hover:shadow-2xl hover:shadow-primary/10"
        )}>
          <CardContent className="w-full p-0 space-y-6">
            {/* Header */}
            <div className="space-y-3 mb-2">
              <Heading level="h1" className="text-4xl font-bold text-foreground tracking-tight">
                Forgot Password?
              </Heading>
              <Text size="base" variant="muted" className="text-muted-foreground leading-relaxed">
                {!isSuccess 
                  ? "Enter your email address and we'll send you a link to reset your password."
                  : "Check your email inbox for the link."
                }
              </Text>
            </div>

            {!isSuccess ? (
              <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div className="relative group/email">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10 transition-colors group-focus-within/email:text-primary" />
                    <Input
                      type="email"
                      id="email"
                      placeholder="Enter your email"
                      aria-label="Email Address"
                      className="w-full pl-10 h-10 transition-all"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                {message && (
                  <div className={cn(
                    "rounded-md p-3 border",
                    isSuccess 
                      ? "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800"
                      : "bg-destructive/10 border-destructive/20"
                  )}>
                    <Text 
                      size="sm" 
                      variant={isSuccess ? "default" : "destructive"} 
                      className={isSuccess ? "text-green-800 dark:text-green-200" : "text-destructive"}
                    >
                      {message}
                    </Text>
                  </div>
                )}

                <Button 
                  type="submit" 
                  variant="default"
                  size="default"
                  className="w-full h-11 font-semibold shadow-sm hover:shadow-md transition-all duration-200"
                  style={{
                    backgroundColor: getColor('primary'),
                    color: getColor('primaryForeground'),
                    borderColor: getColor('primary')
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>

                <div className="pt-2">
                  <Link 
                    to="/hirewithus/login" 
                    className={cn(
                      "flex items-center gap-2 text-sm text-muted-foreground hover:text-primary",
                      "transition-colors underline-offset-4 hover:underline"
                    )}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Login
                  </Link>
                </div>
              </form>
            ) : (
              <div className="flex flex-col items-center text-center space-y-4 py-4">
                <div className="rounded-full bg-green-100 dark:bg-green-900 p-4">
                  <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <Heading level="h3" className="text-2xl font-semibold text-foreground">
                  Check Your Email
                </Heading>
                <Text size="base" variant="muted" className="text-muted-foreground max-w-sm">
                  {message}
                </Text>
                <Text size="sm" variant="muted" className="text-muted-foreground">
                  If you don't see the email, check your spam folder.
                </Text>
                <Link to="/hirewithus/login" className="w-full">
                  <Button
                    variant="default"
                    size="default"
                    className="w-full h-11 font-semibold shadow-sm hover:shadow-md transition-all duration-200 mt-4"
                    style={{
                      backgroundColor: getColor('primary'),
                      color: getColor('primaryForeground'),
                      borderColor: getColor('primary')
                    }}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
        
        <div className="hidden lg:block flex-shrink-0">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-2xl blur-2xl" />
            <img 
              className="relative w-full max-w-[520px] aspect-[0.72] object-cover rounded-2xl shadow-2xl"
              loading="lazy"
              src="/login.jpg"
              alt="Forgot password illustration"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
